import React, { Component, Dispatch } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { routes } from 'routes';

import './PaymentPage.scss';

import { AppState } from 'store';
import { generateDispatchToPropsMap, GtmCheckoutSteps, goToExternalUrl } from 'helpers';
import { Loader, OrderSummaryPanel, BasketModal } from 'components';
import { preprocessRedirectToNextStep, PreprocessRedirectToNextStepFunc, initPaymentPage, InitPaymentPageFunc } from 'store/checkout/actions';
import { PaymentDetailsModel, PaymentTypes } from 'models/Payment';
import { BasketDetailsModel } from 'models/Basket';
import { formatCurrency } from 'formatters';
import { setShowReservationWarning, SetShowReservationWarningFunc } from 'store/basket/actions';
import { basketStorage } from 'storages';
import { Payment } from './Payment';
import { AddNotificationFunc, addNotification } from 'store/notifications/actions';
import { DEFAULT_ERROR_TEXT } from 'models/Errors';
import { NotificationTypes } from 'models';
import { SetShowFinanceApplicationNotValidWarningFunc, setShowFinanceApplicationNotValidWarning } from 'store/checkout/actions/finance';

const FINANCE_APPLICATION_INVALID_ERROR_MESSAGE = 'We apologize for the inconvenience. Due to a page refresh, you were redirected to the beginning of the finance form. Please complete the form again.';


interface OwnProps {
}

interface StateProps {
    basket: BasketDetailsModel | null;
    isPaymentPageInitializing: boolean;
    isPaymentPageInitialized: boolean;
    paymentSuccessfullyCompleted: boolean;
    paymentDetails: PaymentDetailsModel;
    shouldShowReservationWarning: boolean;
    shouldShowFinanceApplicationNotValidWarning: boolean;
}

interface DispatchProps {
    initPaymentPage: InitPaymentPageFunc;
    preprocessRedirectToNextStep: PreprocessRedirectToNextStepFunc;
    setShowReservationWarning: SetShowReservationWarningFunc;
    addNotification: AddNotificationFunc;
    setShowFinanceApplicationNotValidWarning: SetShowFinanceApplicationNotValidWarningFunc;
}

class PaymentPage extends Component<OwnProps & StateProps & DispatchProps & RouteComponentProps> {
    componentDidMount() {
        this.props.initPaymentPage();
    }

    componentDidUpdate(prevProps: OwnProps & StateProps & DispatchProps & RouteComponentProps): void {
        if (!prevProps.paymentSuccessfullyCompleted && this.props.paymentSuccessfullyCompleted) {
            this.props.preprocessRedirectToNextStep(
                () => this.props.history.push(routes.orderPage.path),
                GtmCheckoutSteps.OrderProcessing
            );
        }

        if (prevProps.isPaymentPageInitializing && !this.props.isPaymentPageInitializing && !this.props.isPaymentPageInitialized) {
            this.props.addNotification(DEFAULT_ERROR_TEXT, NotificationTypes.Err);
        }

        // if the basket is empty in the system then we should exclude a case when basket exists in local storage at the same time
        // mentioned case performs handleApiAuthErrorRequestSaga due to auth reasons
        const basketFromLocalStorage = basketStorage.getBasket();
        const shouldRedirect = !this.props.basket?.entries.length && !basketFromLocalStorage?.entries.length;

        if (prevProps.isPaymentPageInitializing && !this.props.isPaymentPageInitializing && shouldRedirect && !this.props.shouldShowReservationWarning) {
            // use external redirect instead of react route - to clear redux state - we are starting journey from scratch
            goToExternalUrl(routes.basket.path);
        }
    }

    handleReservationWarningClick = () => {
        this.props.setShowReservationWarning(false);
        this.props.history.push(routes.basket.path);
    };

    handleFinanceApplicationWarningClick = () => {
        this.props.setShowFinanceApplicationNotValidWarning(false);
        this.props.history.push(routes.financeApplication.path);
    };

    render(): JSX.Element {
        const { isPaymentPageInitializing, shouldShowReservationWarning, isPaymentPageInitialized, shouldShowFinanceApplicationNotValidWarning } = this.props;

        return (
            <div className='payment-page'>
                {
                    (isPaymentPageInitializing || !isPaymentPageInitialized)
                        ? <Loader show={true} />
                        : <>
                            {this.renderPaymentContent()}
                            {this.renderPageSecuredMessage()}
                            <BasketModal open={shouldShowReservationWarning} onClick={this.handleReservationWarningClick} />
                            <BasketModal
                                open={shouldShowFinanceApplicationNotValidWarning}
                                onClick={this.handleFinanceApplicationWarningClick}
                                title={FINANCE_APPLICATION_INVALID_ERROR_MESSAGE}
                            />
                        </>
                }
            </div>
        );
    }

    renderPaymentContent(): JSX.Element {
        if (this.props.paymentDetails.paymentType === PaymentTypes.IFC) {
            return this.renderDepositPaymentContent();
        }

        return this.renderFullPaymentContent();
    }

    renderFullPaymentContent(): JSX.Element {
        return (
            <div className='payment-page__content'>
                <Payment />
                <OrderSummaryPanel useDateFromApi={true} />
            </div>
        );
    }

    renderPageSecuredMessage(): JSX.Element {
        return (
            <div className='payment-page__page-secured-message'>
                This page is secure. <br />
                All of your personal details are safe.
            </div>
        );
    }

    renderDepositPaymentContent(): JSX.Element {
        const { ifcDeposit, ifcMonthlyAmount, ifcNumMonths } = this.props.paymentDetails;

        return (
            <div className='payment-page__content'>
                <div className='payment-page__content-deposit'>
                    <div className='payment-page__content-deposit__title'>
                        Your deposit payment
                    </div>
                    <div className='payment-page__content-deposit__text'>
                        You chose an initial deposit of <b>{formatCurrency(ifcDeposit)}</b> to pay now with <b>{ifcNumMonths} monthly payments of {formatCurrency(ifcMonthlyAmount)}</b>
                    </div>
                </div>
                <Payment />
            </div>
        );
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        basket: state.basketState.basket,
        isPaymentPageInitializing: state.checkoutState.isPaymentPageInitializing,
        isPaymentPageInitialized: state.checkoutState.isPaymentPageInitialized,
        paymentSuccessfullyCompleted: state.paymentState.paymentSuccessfullyCompleted,
        paymentDetails: state.paymentState.paymentDetails,
        shouldShowReservationWarning: state.basketState.shouldShowReservationWarning,
        shouldShowFinanceApplicationNotValidWarning: state.checkoutState.shouldShowFinanceApplicationNotValidWarning
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [
        initPaymentPage,
        preprocessRedirectToNextStep,
        setShowReservationWarning,
        setShowFinanceApplicationNotValidWarning,
        addNotification
    ]);
}

export default connect(mapStateToProps, mapDispatchToProps)(PaymentPage);